// One list of the shapes the app offers (Phase 6, D-033): node types, sidebar and the shape dropdown read it.
import { MarkerType } from 'Custom-Mindmap';

// The fills of the colour palette above a selected shape, by name.
export const NODE_COLORS = {
    red: '#CF4C2C',
    orange: '#EA9C41',
    yellow: '#EBC347',
    green: '#438D57',
    blue: '#3F8AE2',
    purple: '#803DEC',
};
// New shapes start blue, like the React Flow Shapes example; a shape dropped from the sidebar takes its category's colour.
export const DEFAULT_NODE_COLOR = NODE_COLORS.blue;
// No fill: the shape shows only a grey outline (the library draws it, D-035).
export const TRANSPARENT_COLOR = 'transparent';

// The colour palette above a selected shape (red, orange, yellow, green, blue, purple, no fill).
export const PALETTE_COLORS = [...Object.values(NODE_COLORS), TRANSPARENT_COLOR];
// Resize box and handles of a selected shape.
export const SELECTION_COLOR = '#3F8AE2';

// How every edge is drawn: right angles with rounded corners and a closed arrow at the target. Display only;
// saved edges are not changed.
export const EDGE_OPTIONS = { type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } };

// Node types drawn as a shape, with the size of a new node. The sidebar's library (Sidebar/shapeLibrary.js) offers
// them under its own names and categories.
export const SHAPES = [
    { type: 'rectangle', label: 'Rectangle', width: 100, height: 60 },
    { type: 'round-rectangle', label: 'Rounded rectangle', width: 120, height: 70 },
    { type: 'square', label: 'Square', width: 100, height: 80 },
    { type: 'circle', label: 'Circle', width: 100, height: 100 },
    { type: 'ellipse', label: 'Ellipse', width: 120, height: 80 },
    { type: 'oval', label: 'Tall ellipse', width: 68, height: 100 },
    { type: 'triangle', label: 'Triangle', width: 160, height: 100 },
    { type: 'diamond', label: 'Diamond', width: 100, height: 100 },
    { type: 'hexagon', label: 'Hexagon', width: 120, height: 80 },
    { type: 'pentagon', label: 'Pentagon', width: 100, height: 100 },
    { type: 'octagon', label: 'Octagon', width: 100, height: 100 },
    { type: 'star', label: 'Star', width: 100, height: 100 },
    { type: 'plus', label: 'Plus', width: 90, height: 90 },
    { type: 'parallelogram', label: 'Parallelogram', width: 100, height: 60 },
    { type: 'trapezoid', label: 'Trapezoid', width: 120, height: 70 },
    { type: 'start-end', label: 'Start or end', width: 140, height: 60 },
    { type: 'cylinder', label: 'Database', width: 90, height: 110 },
    { type: 'document', label: 'Document', width: 120, height: 80 },
    { type: 'predefined-process', label: 'Predefined process', width: 140, height: 70 },
    { type: 'manual-input', label: 'Manual input', width: 120, height: 70 },
    { type: 'delay', label: 'Delay', width: 110, height: 70 },
    { type: 'off-page-link', label: 'Off-page link', width: 90, height: 100 },
    { type: 'arrow', label: 'Arrow', width: 130, height: 70 },
    { type: 'double-arrow', label: 'Double arrow', width: 150, height: 70 },
    { type: 'arrow-up', label: 'Arrow up', width: 80, height: 110 },
    { type: 'chevron', label: 'Chevron', width: 120, height: 70 },
    { type: 'arrow-rectangle', label: 'Arrow rectangle', width: 120, height: 60 },
    { type: 'speech-bubble', label: 'Speech bubble', width: 140, height: 100 },
    { type: 'cloud', label: 'Cloud', width: 140, height: 90 },
    { type: 'sticky-note', label: 'Sticky note', width: 110, height: 110 },
    { type: 'heart', label: 'Heart', width: 100, height: 90 },
];

// Text-box node types that are not drawn as a shape outline of their own choosing.
export const TEXT_NODE_TYPES = [
    { type: 'custom', label: 'Custom', width: 150, height: 35 },
    { type: 'text', label: 'Plain Text', width: 150, height: 35 },
];

// A node's border (D-045): `data.borderStyle` ('solid' | 'dashed' | 'dotted' | 'none'; missing = solid) and
// `data.borderWidth` (px; missing = the shape's own 2px). Its colour is not per node: see outlineOf.
export const BORDER_STYLES = { solid: 'solid', dashed: 'dashed', dotted: 'dotted', none: 'none' };
export const DEFAULT_BORDER_WIDTH = 2;
const DASH_PATTERNS = {
    [BORDER_STYLES.dashed]: (width) => `${width * 6} ${width * 3}`,
    [BORDER_STYLES.dotted]: (width) => `${width} ${width * 2}`,
};

// How a node's outline is drawn, as `<Shape>` props. Its colour is the creator's colour (`allocatedColor`, the
// colour dot in the header; D-043), else left to the shape, which uses a darker shade of the fill. "No border"
// draws none.
export const outlineOf = (data) => {
    if (data?.borderStyle === BORDER_STYLES.none) return { strokeWidth: 0 };
    const strokeWidth = data?.borderWidth || undefined;
    return {
        outlineColor: data?.allocatedColor || undefined,
        strokeWidth,
        strokeDasharray: DASH_PATTERNS[data?.borderStyle]?.(strokeWidth ?? DEFAULT_BORDER_WIDTH),
    };
};

// Size a node of `type` shows before it is resized.
export const defaultSizeOf = (type) => {
    const shape = [...SHAPES, ...TEXT_NODE_TYPES].find((candidate) => candidate.type === type);
    return shape ? { width: shape.width, height: shape.height } : null;
};

// The minimap draws each node in its own fill; white and "no fill" nodes in grey so they show on the light board.
const MINIMAP_NEUTRAL = '#C9CEDB';
const PALE_FILLS = ['#fff', '#ffffff', 'white', TRANSPARENT_COLOR];
export const minimapColorOf = (node) => {
    const fill = node.data?.nodeBg;
    return !fill || PALE_FILLS.includes(String(fill).toLowerCase()) ? MINIMAP_NEUTRAL : fill;
};

// Minimap look of the redesign: a blue outline for the visible area instead of a grey mask.
export const MINIMAP_OPTIONS = {
    maskColor: 'transparent',
    maskStrokeColor: '#2F6BFF',
    maskStrokeWidth: 1.5,
    nodeBorderRadius: 6,
    width: 190,
    height: 120,
};
